import { z } from "zod";
import { defaultDeductibleCents, defaultIncludedLines, groupKey } from "./build";
import { centsToDollars, dollarsToCents } from "./money";
import type { EstimateExtraction, ProposalBuildRequest, ProposalCustomer, ProposalProduct } from "./types";
import { DEFAULT_PROJECT_NOTE } from "./wording";

/**
 * The rep's proposal form: include/exclude checkboxes, group headings, deal details, and the
 * chat assistant's edits. Ported from the Worker's proposal page script (proposalPage.html),
 * which kept this state in the browser and sent it with every request.
 */

/** Everything the rep can change on the proposal form. Amounts in cents. */
export interface ProposalFormState {
  /** Estimate line numbers in the contract. */
  included: number[];
  /** Group key → heading/summary overrides. */
  groups: Record<string, { heading?: string; summary?: string }>;
  deductibleCents: number;
  product: ProposalProduct;
  /** Upgrade descriptions, one per row (may include blank rows being typed). */
  upgrades: { items: string[]; noCharge: string[]; totalCents: number };
  /** Project notes, one per line. */
  notes: string[];
  signerName: string;
  signerEmail: string;
}

/** The form right after an estimate is read: every non-owner line included, deductible from the estimate, Duration FLEX. */
export function initialFormState(ex: EstimateExtraction, customer: { name: string; email?: string }): ProposalFormState {
  return {
    included: defaultIncludedLines(ex),
    groups: {},
    deductibleCents: defaultDeductibleCents(ex),
    product: { key: "flex" },
    upgrades: { items: [], noCharge: [], totalCents: 0 },
    notes: [DEFAULT_PROJECT_NOTE],
    signerName: customer.name,
    signerEmail: customer.email ?? "",
  };
}

/** Checks or unchecks one line. */
export function setLineIncluded(state: ProposalFormState, n: number, on: boolean): ProposalFormState {
  const inc = new Set(state.included);
  if (on) inc.add(n);
  else inc.delete(n);
  return { ...state, included: [...inc] };
}

/** Checks or unchecks every line of a "<trade> - <building>" group. */
export function setGroupIncluded(state: ProposalFormState, ex: EstimateExtraction, key: string, on: boolean): ProposalFormState {
  const inc = new Set(state.included);
  for (const i of ex.items) if (groupKey(i) === key) on ? inc.add(i.n) : inc.delete(i.n);
  return { ...state, included: [...inc] };
}

/** "Include all" (every non-owner line) / "Exclude all". */
export function setAllIncluded(state: ProposalFormState, ex: EstimateExtraction, on: boolean): ProposalFormState {
  return { ...state, included: on ? defaultIncludedLines(ex) : [] };
}

const MARKER = /^\s*(\d{1,2}[.)]|[-•*])\s+/;

/**
 * Upgrade lines as typed or pasted → one item each. A line pasted from a PDF often breaks mid-sentence:
 * a line starting in lowercase continues the one before, and when the items are numbered or bulleted,
 * a line without a number or bullet does too. The numbers and bullets themselves go (the PDF numbers them).
 */
export function upgradeItems(lines: readonly string[]): string[] {
  const marked = lines.some((l) => MARKER.test(l));
  const out: string[] = [];
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) continue;
    const continues = out.length > 0 && ((marked && !MARKER.test(raw)) || /^[a-z(]/.test(l));
    if (continues) out[out.length - 1] = `${out[out.length - 1]} ${l}`;
    else out.push(l.replace(MARKER, "").trim());
  }
  return out.filter(Boolean);
}

/** The build request for the form (Worker page `buildReq()`): blank rows dropped, customer = signer over the job's customer. */
export function buildRequestFromForm(state: ProposalFormState, customer: ProposalCustomer): ProposalBuildRequest {
  return {
    included: state.included,
    groups: state.groups,
    deductibleCents: state.deductibleCents || 0,
    product: state.product,
    upgrades: {
      items: upgradeItems(state.upgrades.items).map((d) => ({ description: d })),
      noCharge: state.upgrades.noCharge.filter((x) => x.trim()),
      totalCents: state.upgrades.totalCents || 0,
    },
    additionalTerms: state.notes.filter((x) => x.trim()),
    customer: { ...customer, name: state.signerName || customer.name, email: state.signerEmail || customer.email },
  };
}

/**
 * The form state as the Worker showed it to the chat assistant (`{ ...buildReq(), notes }`),
 * with amounts in dollars, so `chatSystemPrompt` reads exactly as it did.
 */
export function chatFormState(state: ProposalFormState, customer: ProposalCustomer): Record<string, unknown> {
  const req = buildRequestFromForm(state, customer);
  return {
    included: req.included,
    groups: req.groups,
    deductible: centsToDollars(req.deductibleCents),
    product: req.product,
    upgrades: { items: req.upgrades!.items, noCharge: req.upgrades!.noCharge, total: centsToDollars(req.upgrades!.totalCents ?? 0) },
    additionalTerms: req.additionalTerms,
    customer: { name: req.customer.name, email: req.customer.email },
    notes: state.notes,
  };
}

export const CHAT_OPS = ["include", "exclude", "set_group", "set_deductible", "set_product", "set_upgrade_items", "set_no_charge", "set_upgrade_total", "set_notes", "set_signer"] as const;

/** One edit from the chat assistant's `apply_changes` tool. `value` is dollars. */
export const chatOpSchema = z.looseObject({
  op: z.enum(CHAT_OPS),
  lines: z.array(z.number().int()).optional(),
  key: z.string().optional(),
  heading: z.string().optional(),
  summary: z.string().optional(),
  value: z.number().finite().optional(),
  product: z.enum(["flex", "duration", "other"]).optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  items: z.array(z.string()).optional(),
  email: z.string().optional(),
});
export type ChatOp = z.infer<typeof chatOpSchema>;

/** The assistant's answer: a reply for the rep and the edits. */
export const chatResponseSchema = z.object({ reply: z.string(), ops: z.array(chatOpSchema).default([]) });
export type ChatResponse = z.infer<typeof chatResponseSchema>;

/**
 * Applies the assistant's edits to the form (Worker page `applyOps()`). The assistant can't
 * change any estimate amount; it only includes/excludes lines and edits the rep's fields.
 * Line numbers not on the estimate are dropped. Dollar values are converted to cents.
 */
export function applyChatOps(state: ProposalFormState, ops: readonly ChatOp[], ex: EstimateExtraction): ProposalFormState {
  const next: ProposalFormState = structuredClone(state);
  const inc = new Set(next.included);
  for (const o of ops) {
    if (o.op === "include") (o.lines ?? []).forEach((n) => inc.add(n));
    if (o.op === "exclude") (o.lines ?? []).forEach((n) => inc.delete(n));
    if (o.op === "set_group" && o.key)
      next.groups[o.key] = { ...(next.groups[o.key] ?? {}), ...(o.heading != null ? { heading: o.heading } : {}), ...(o.summary != null ? { summary: o.summary } : {}) };
    if (o.op === "set_deductible" && o.value != null) next.deductibleCents = dollarsToCents(o.value);
    if (o.op === "set_product" && o.product) {
      const p: ProposalProduct = { key: o.product };
      const name = o.name ?? next.product.name, description = o.description ?? next.product.description;
      if (name !== undefined) p.name = name;
      if (description !== undefined) p.description = description;
      next.product = p;
    }
    if (o.op === "set_upgrade_items") next.upgrades.items = o.items ?? [];
    if (o.op === "set_no_charge") next.upgrades.noCharge = o.items ?? [];
    if (o.op === "set_upgrade_total" && o.value != null) next.upgrades.totalCents = dollarsToCents(o.value);
    if (o.op === "set_notes") next.notes = o.items ?? [];
    if (o.op === "set_signer" && o.email) next.signerEmail = o.email;
  }
  next.included = [...inc].filter((n) => ex.items.some((i) => i.n === n));
  return next;
}
